/**
 * SupervisionAssignmentDialog Component
 * 
 * Task 11.1: Dialog for assigning teachers to supervision duties during breaks,
 * assemblies, and other non-instructional periods
 * 
 * Requirements: 22.1, 22.6, 22.7, 22.8, 22.9, 22.10, 22.11
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Typography,
  Stack,
  Paper,
  Chip,
  SelectChangeEvent,
  Autocomplete,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  Security as SupervisionIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getTeachersDirect,
  createTimetableRecordDirect,
  updateTimetableRecordDirect,
} from '@timetable/api';
import type { 
  TimetableRecordWithRelations, 
  DayOfWeek,
  Teacher,
  SchedulePeriod,
} from '@timetable/types';
import { useAuthStore } from '../../store/authStore';
import { useToast } from '../Toast';
import { LoadingSpinner } from '../LoadingSpinner';
import { queryKeys } from '../../lib/queryKeys';

// ============================================================================
// TypeScript Interfaces
// ============================================================================

interface SupervisionAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  sectionId: string;
  period: SchedulePeriod;
  day: DayOfWeek;
  existingRecords?: TimetableRecordWithRelations[];
  onSave?: () => void;
  academicYearId?: string;
  academicYear?: string;
}

interface SupervisionAssignmentData {
  teacherId: string;
  location?: string;
  notes?: string;
}

interface TeacherWorkloadInfo {
  teacherId: string;
  teachingPeriods: number;
  supervisionDuties: number;
  totalWorkload: number;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Convert DayOfWeek (uppercase) to API format — schema requires UPPERCASE
 */
function convertDayOfWeekToApiFormat(day: DayOfWeek): DayOfWeek {
  return day; // DayOfWeek is already UPPERCASE — pass through directly
}

/**
 * Get supervision type display name
 * Requirement 22.6: Display supervision type
 */
function getSupervisionTypeDisplay(type: string | null): string {
  if (!type) return 'Supervision';
  
  const typeMap: Record<string, string> = {
    break: 'Break Duty',
    lunch: 'Lunch Duty',
    assembly: 'Assembly Duty',
    recess: 'Recess Duty',
  };
  
  return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1) + ' Duty';
}

/**
 * Calculate teacher workload (mock implementation)
 * Requirement 22.11: Display teacher workload information
 * TODO: Replace with actual API call to get workload data
 */
function calculateTeacherWorkload(
  teachers: Teacher[] | undefined,
  existingRecords: TimetableRecordWithRelations[] | undefined
): Map<string, TeacherWorkloadInfo> {
  const workloadMap = new Map<string, TeacherWorkloadInfo>();
  
  if (!teachers || !existingRecords) return workloadMap;
  
  teachers.forEach(teacher => {
    const teachingPeriods = existingRecords.filter(
      r => r.teacher_id === teacher.id && !r.is_supervision
    ).length;
    
    const supervisionDuties = existingRecords.filter(
      r => r.teacher_id === teacher.id && r.is_supervision
    ).length;
    
    workloadMap.set(teacher.id, {
      teacherId: teacher.id,
      teachingPeriods,
      supervisionDuties,
      totalWorkload: teachingPeriods + supervisionDuties,
    });
  });
  
  return workloadMap;
}

/**
 * Check if multiple teachers are allowed for this supervision type
 * Requirement 22.8: Support multiple teachers for assembly, single for breaks
 */
function allowsMultipleTeachers(supervisionType: string | null): boolean {
  return supervisionType === 'assembly';
}

// ============================================================================
// Main Component
// ============================================================================

export function SupervisionAssignmentDialog({
  open,
  onClose,
  sectionId,
  period,
  day,
  existingRecords = [],
  onSave,
  academicYearId,
  academicYear,
}: SupervisionAssignmentDialogProps) {
  const { schoolContext, user } = useAuthStore();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Requirement 22.7: Allow selecting one or more teachers
  const [selectedTeachers, setSelectedTeachers] = useState<Teacher[]>([]);
  // Requirement 22.9: Allow specifying supervision location
  const [location, setLocation] = useState('');
  // Requirement 22.10: Allow adding notes
  const [notes, setNotes] = useState('');

  const multipleTeachersAllowed = allowsMultipleTeachers(period.supervision_type);

  // Reset form when dialog opens/closes or records change
  useEffect(() => {
    if (open) {
      // Pre-populate with existing supervision assignments
      const existingSupervision = existingRecords.filter(
        r => r.is_supervision && r.period_id === period.id && r.day_of_week === convertDayOfWeekToApiFormat(day)
      );
      
      if (existingSupervision.length > 0) {
        // Load existing teachers
        const existingTeacherIds = existingSupervision.map(r => r.teacher_id);
        // We'll set this after teachers are loaded
        setLocation(existingSupervision[0].supervision_location || '');
        setNotes(existingSupervision[0].supervision_notes || '');
      } else {
        setSelectedTeachers([]);
        setLocation('');
        setNotes('');
      }
    }
  }, [open, existingRecords, period.id, day]);

  // Fetch teachers
  const { data: teachers, isLoading: loadingTeachers } = useQuery({
    queryKey: queryKeys.teachers.list({ school_id: schoolContext || '' }),
    queryFn: () => getTeachersDirect({ school_id: schoolContext || '' }),
    enabled: open && !!schoolContext,
  });

  // Fetch all timetable records for workload calculation
  const { data: allRecords } = useQuery({
    queryKey: queryKeys.timetable.list({ school_id: schoolContext || '' }),
    queryFn: async () => {
      // TODO: Implement proper API call to fetch timetable records
      return [] as TimetableRecordWithRelations[];
    },
    enabled: open && !!schoolContext,
  });

  // Calculate teacher workload
  // Requirement 22.11: Display teacher workload information
  const teacherWorkload = calculateTeacherWorkload(teachers, allRecords);

  // Pre-populate selected teachers after teachers are loaded
  useEffect(() => {
    if (open && teachers && existingRecords.length > 0) {
      const existingSupervision = existingRecords.filter(
        r => r.is_supervision && r.period_id === period.id && r.day_of_week === convertDayOfWeekToApiFormat(day)
      );
      
      if (existingSupervision.length > 0) {
        const existingTeacherIds = existingSupervision.map(r => r.teacher_id);
        const existingTeacherObjects = teachers.filter(t => existingTeacherIds.includes(t.id));
        setSelectedTeachers(existingTeacherObjects);
      }
    }
  }, [open, teachers, existingRecords, period.id, day]);

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!schoolContext) throw new Error('No school context');
      if (selectedTeachers.length === 0) throw new Error('Please select at least one teacher');

      // Requirement 22.23: Store supervision assignments with is_supervision=true
      const promises = selectedTeachers.map(teacher => {
        const data = {
          school_id: schoolContext,
          class_section_id: sectionId,
          period_id: period.id,
          day_of_week: convertDayOfWeekToApiFormat(day),
          teacher_id: teacher.id,
          subject_id: null,
          academic_year: academicYear ?? '',
          academic_year_id: academicYearId ?? null,
          valid_from: new Date().toISOString().split('T')[0],
          created_by: user?.id,
          is_supervision: true,
          supervision_location: location || null,
          supervision_notes: notes || null,
        };

        // Check if this teacher already has a supervision assignment for this period
        const existingRecord = existingRecords.find(
          r => r.is_supervision && 
               r.teacher_id === teacher.id && 
               r.period_id === period.id && 
               r.day_of_week === convertDayOfWeekToApiFormat(day)
        );

        if (existingRecord) {
          return updateTimetableRecordDirect(existingRecord.id, data);
        } else {
          return createTimetableRecordDirect(data);
        }
      });

      return Promise.all(promises);
    },
    onSuccess: () => {
      toast.success('Supervision duty assigned successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.timetable.all });
      onSave?.();
      handleClose();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to assign supervision duty');
    },
  });

  const handleClose = () => {
    setSelectedTeachers([]);
    setLocation('');
    setNotes('');
    onClose();
  };

  const handleTeacherChange = (event: any, value: Teacher | Teacher[] | null) => {
    if (multipleTeachersAllowed) {
      // Requirement 22.8: Multiple teachers for assembly
      setSelectedTeachers(Array.isArray(value) ? value : []);
    } else {
      // Requirement 22.8: Single teacher for breaks
      setSelectedTeachers(value ? [value as Teacher] : []);
    }
  };

  const handleLocationChange = (event: SelectChangeEvent<string>) => {
    setLocation(event.target.value);
  };

  const handleNotesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNotes(event.target.value);
  };

  const handleSave = () => {
    if (selectedTeachers.length === 0) {
      toast.error('Please select at least one teacher');
      return;
    }
    saveMutation.mutate();
  };

  const isFormValid = () => {
    return selectedTeachers.length > 0;
  };

  const isLoading = loadingTeachers;

  // Common supervision locations
  const commonLocations = [
    'Playground',
    'Cafeteria',
    'Corridor',
    'Assembly Hall',
    'Library',
    'Main Gate',
    'Sports Field',
    'Courtyard',
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="supervision-assignment-dialog-title"
    >
      <DialogTitle id="supervision-assignment-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SupervisionIcon color="success" />
          <Typography variant="h6" component="span">
            Assign Supervision Duty
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <LoadingSpinner />
          </Box>
        ) : (
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Supervision Type Display - Requirement 22.6 */}
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.50', borderColor: 'success.main' }}>
              <Typography variant="subtitle2" color="success.dark" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SupervisionIcon fontSize="small" />
                Supervision Details
              </Typography>
              <Stack spacing={0.5}>
                <Typography variant="body2">
                  <strong>Type:</strong> {getSupervisionTypeDisplay(period.supervision_type)}
                </Typography>
                <Typography variant="body2">
                  <strong>Day:</strong> {day}
                </Typography>
                <Typography variant="body2">
                  <strong>Time:</strong> {period.time_start} - {period.time_end}
                </Typography>
                {period.period_name && (
                  <Typography variant="body2">
                    <strong>Period:</strong> {period.period_name}
                  </Typography>
                )}
              </Stack>
            </Paper>

            {/* Info Alert for Multiple Teachers - Requirement 22.8 */}
            {multipleTeachersAllowed ? (
              <Alert severity="info" icon={<InfoIcon />}>
                <Typography variant="body2">
                  <strong>Assembly Duty:</strong> You can assign multiple teachers for assembly supervision.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="info" icon={<InfoIcon />}>
                <Typography variant="body2">
                  <strong>Break Duty:</strong> Please assign one teacher for this supervision period.
                </Typography>
              </Alert>
            )}

            {/* Teacher Selection - Requirements 22.7, 22.8 */}
            <FormControl fullWidth required>
              <Autocomplete
                multiple={multipleTeachersAllowed}
                id="teacher-select"
                options={teachers || []}
                getOptionLabel={(option) => option.name}
                value={multipleTeachersAllowed ? selectedTeachers : (selectedTeachers[0] || null)}
                onChange={handleTeacherChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={multipleTeachersAllowed ? 'Select Teachers *' : 'Select Teacher *'}
                    placeholder={multipleTeachersAllowed ? 'Choose one or more teachers' : 'Choose a teacher'}
                  />
                )}
                renderOption={(props, option) => {
                  const workload = teacherWorkload.get(option.id);
                  return (
                    <li {...props}>
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2">{option.name}</Typography>
                        </Box>
                        {/* Requirement 22.11: Display teacher workload information */}
                        {workload && (
                          <Box sx={{ pl: 3 }}>
                            <Typography variant="caption" color="text.secondary">
                              Teaching: {workload.teachingPeriods} periods | Supervision: {workload.supervisionDuties} duties
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min((workload.totalWorkload / 40) * 100, 100)}
                              sx={{ 
                                mt: 0.5, 
                                height: 4, 
                                borderRadius: 2,
                                bgcolor: 'grey.200',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: workload.totalWorkload > 35 ? 'error.main' : workload.totalWorkload > 25 ? 'warning.main' : 'success.main',
                                }
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </li>
                  );
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.name}
                      {...getTagProps({ index })}
                      size="small"
                      icon={<PersonIcon />}
                    />
                  ))
                }
              />
            </FormControl>

            {/* Location Selection - Requirement 22.9 */}
            <FormControl fullWidth>
              <InputLabel id="location-label">Supervision Location</InputLabel>
              <Select
                labelId="location-label"
                id="location-select"
                value={location}
                label="Supervision Location"
                onChange={handleLocationChange}
              >
                <MenuItem value="">
                  <em>Not specified</em>
                </MenuItem>
                {commonLocations.map((loc) => (
                  <MenuItem key={loc} value={loc}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon fontSize="small" color="action" />
                      {loc}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Notes - Requirement 22.10 */}
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes (optional)"
              placeholder="Add any additional notes about this supervision duty..."
              value={notes}
              onChange={handleNotesChange}
              helperText="Optional notes about the supervision duty"
            />

            {/* Selected Teachers Summary */}
            {selectedTeachers.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Assigned Teachers ({selectedTeachers.length})
                </Typography>
                <List dense>
                  {selectedTeachers.map((teacher) => {
                    const workload = teacherWorkload.get(teacher.id);
                    return (
                      <ListItem key={teacher.id} sx={{ px: 0 }}>
                        <ListItemText
                          primary={teacher.name}
                          secondary={
                            workload
                              ? `Teaching: ${workload.teachingPeriods} | Supervision: ${workload.supervisionDuties} | Total: ${workload.totalWorkload}`
                              : 'Workload information unavailable'
                          }
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="success"
          disabled={!isFormValid() || saveMutation.isPending}
          startIcon={saveMutation.isPending ? <CircularProgress size={16} /> : <SupervisionIcon />}
        >
          {saveMutation.isPending ? 'Assigning...' : 'Assign Supervision'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
